import { createSvgEl } from '../svg';
import { type Vec2, dist } from '@learnforge/demo-math/geometry';

const SNAP_TOLERANCE = 12; // screen-space px

export type CompassTool = 'compass' | 'straightedge' | 'eraser' | 'none';

export interface SnapPoint {
  id: string;
  at: Vec2;
}

export interface DrawableObject {
  id: string;
}

export type CompassToolEvent =
  | { type: 'objectDrawn'; tool: 'compass'; center: string; through: string }
  | { type: 'objectDrawn'; tool: 'straightedge'; through: [string, string] }
  | { type: 'objectErased'; id: string };

export interface CompassToolsOptions {
  parent: SVGElement;
  screenToWorld?: (screen: Vec2) => Vec2;
  worldToScreen?: (world: Vec2) => Vec2;
  hitTest?: (at: Vec2) => string | null;
}

export interface CompassToolsHandle {
  setTool(tool: CompassTool): void;
  setSnapPoints(points: readonly SnapPoint[]): void;
  on(cb: (e: CompassToolEvent) => void): void;
  destroy(): void;
}

export function createCompassTools(options: CompassToolsOptions): CompassToolsHandle {
  const {
    parent,
    screenToWorld = (p: Vec2) => p,
    worldToScreen = (p: Vec2) => p,
    hitTest = () => null,
  } = options;

  let currentTool: CompassTool = 'none';
  let snapPoints: SnapPoint[] = [];
  let eventCallback: ((e: CompassToolEvent) => void) | null = null;

  // State machine for compass and straightedge tools
  type InteractionState =
    | { kind: 'idle' }
    | {
        kind: 'compass-centre-picked';
        centerId: string;
        centreAt: Vec2;
      }
    | {
        kind: 'straightedge-first-picked';
        firstId: string;
        firstAt: Vec2;
      };

  let state: InteractionState = { kind: 'idle' };

  // SVG containers
  const dotsGroup = createSvgEl('g', { 'data-role': 'snap-dots-group' });
  parent.appendChild(dotsGroup);

  const previewsGroup = createSvgEl('g', { 'data-role': 'previews-group' });
  parent.appendChild(previewsGroup);

  const dotsById = new Map<string, SVGCircleElement>();

  const renderSnapDots = (): void => {
    dotsGroup.innerHTML = '';
    dotsById.clear();

    for (const point of snapPoints) {
      const dot = createSvgEl('circle', {
        cx: point.at.x,
        cy: point.at.y,
        r: 3,
        'data-role': 'snap-dot',
        'data-id': point.id,
        class: 'geo-snap-dot',
      });

      dot.addEventListener('pointerover', handleDotHover);
      dot.addEventListener('pointerout', handleDotOut);
      dot.addEventListener('click', handleSnapDotClick);

      dotsGroup.appendChild(dot);
      dotsById.set(point.id, dot);
    }
  };

  const handleDotHover = (evt: Event): void => {
    const dot = evt.target as SVGCircleElement;
    dot.classList.add('geo-snap-brightened');
  };

  const handleDotOut = (evt: Event): void => {
    const dot = evt.target as SVGCircleElement;
    dot.classList.remove('geo-snap-brightened');
  };

  // Find the nearest snap point within screen-space tolerance.
  // Currently unused but kept for future proximity-based snapping features.
  // Compares distances in screen space by transforming world snap points via worldToScreen.
  // Intentionally kept as dead code; tested to ensure screen-space distance works correctly
  // with non-identity worldToScreen transforms.
  const findNearestSnapPoint = (screenPos: Vec2): SnapPoint | null => {
    let nearest: SnapPoint | null = null;
    let minDist = SNAP_TOLERANCE;

    for (const point of snapPoints) {
      const pointScreenPos = worldToScreen(point.at);
      const d = dist(screenPos, pointScreenPos);
      if (d < minDist) {
        minDist = d;
        nearest = point;
      }
    }

    return nearest;
  };
  void findNearestSnapPoint;

  const removePreview = (): void => {
    previewsGroup.innerHTML = '';
  };

  const handleSnapDotClick = (evt: Event): void => {
    const dot = evt.target as SVGCircleElement;
    const pointerId = dot.getAttribute('data-id');
    if (!pointerId) return;

    const point = snapPoints.find((p) => p.id === pointerId);
    if (!point) return;

    if (currentTool === 'compass') {
      if (state.kind === 'idle') {
        state = { kind: 'compass-centre-picked', centerId: point.id, centreAt: point.at };
        // Create and show preview circle
        const preview = createSvgEl('circle', {
          cx: point.at.x,
          cy: point.at.y,
          r: 0,
          'data-role': 'compass-preview',
          class: 'geo-compass-preview',
        });
        previewsGroup.appendChild(preview);
        document.addEventListener('pointermove', handleCompassPreviewMove);
      } else if (state.kind === 'compass-centre-picked') {
        // Second click: emit objectDrawn
        eventCallback?.({
          type: 'objectDrawn',
          tool: 'compass',
          center: state.centerId,
          through: point.id,
        });
        removePreview();
        document.removeEventListener('pointermove', handleCompassPreviewMove);
        state = { kind: 'idle' };
      }
    } else if (currentTool === 'straightedge') {
      if (state.kind === 'idle') {
        state = { kind: 'straightedge-first-picked', firstId: point.id, firstAt: point.at };
        // Create and show preview line
        const preview = createSvgEl('line', {
          x1: point.at.x,
          y1: point.at.y,
          x2: point.at.x,
          y2: point.at.y,
          'data-role': 'straightedge-preview',
          class: 'geo-straightedge-preview',
        });
        previewsGroup.appendChild(preview);
        document.addEventListener('pointermove', handleStraightedgePreviewMove);
      } else if (state.kind === 'straightedge-first-picked') {
        // Second click: emit objectDrawn
        eventCallback?.({
          type: 'objectDrawn',
          tool: 'straightedge',
          through: [state.firstId, point.id],
        });
        removePreview();
        document.removeEventListener('pointermove', handleStraightedgePreviewMove);
        state = { kind: 'idle' };
      }
    }
  };

  const handleCompassPreviewMove = (evt: PointerEvent): void => {
    if (state.kind !== 'compass-centre-picked') return;

    const worldPos = screenToWorld({ x: evt.clientX, y: evt.clientY });
    const radius = dist(state.centreAt, worldPos);

    const preview = previewsGroup.querySelector(
      '[data-role="compass-preview"]',
    ) as SVGCircleElement;
    if (preview) {
      preview.setAttribute('r', String(radius));
    }
  };

  const handleStraightedgePreviewMove = (evt: PointerEvent): void => {
    if (state.kind !== 'straightedge-first-picked') return;

    const worldPos = screenToWorld({ x: evt.clientX, y: evt.clientY });

    const preview = previewsGroup.querySelector(
      '[data-role="straightedge-preview"]',
    ) as SVGLineElement;
    if (preview) {
      preview.setAttribute('x2', String(worldPos.x));
      preview.setAttribute('y2', String(worldPos.y));
    }
  };

  const handleDocumentClick = (evt: PointerEvent): void => {
    if (currentTool === 'eraser') {
      const screenPos = { x: evt.clientX, y: evt.clientY };
      const worldPos = screenToWorld(screenPos);
      const objectId = hitTest(worldPos);
      if (objectId) {
        eventCallback?.({
          type: 'objectErased',
          id: objectId,
        });
      }
    }
  };

  const handleKeyDown = (evt: KeyboardEvent): void => {
    if (evt.key === 'Escape') {
      if (state.kind !== 'idle') {
        removePreview();
        document.removeEventListener('pointermove', handleCompassPreviewMove);
        document.removeEventListener('pointermove', handleStraightedgePreviewMove);
        state = { kind: 'idle' };
      }
    }
  };

  const handle: CompassToolsHandle = {
    setTool(tool: CompassTool): void {
      // Cancel any in-progress interaction
      if (state.kind !== 'idle') {
        removePreview();
        document.removeEventListener('pointermove', handleCompassPreviewMove);
        document.removeEventListener('pointermove', handleStraightedgePreviewMove);
        state = { kind: 'idle' };
      }

      currentTool = tool;

      if (tool === 'none') {
        document.removeEventListener('click', handleDocumentClick);
        document.removeEventListener('keydown', handleKeyDown);
      } else {
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleKeyDown);
      }
    },

    setSnapPoints(points: readonly SnapPoint[]): void {
      snapPoints = Array.from(points);
      renderSnapDots();
    },

    on(cb: (e: CompassToolEvent) => void): void {
      eventCallback = cb;
    },

    destroy(): void {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointermove', handleCompassPreviewMove);
      document.removeEventListener('pointermove', handleStraightedgePreviewMove);

      for (const dot of dotsById.values()) {
        dot.removeEventListener('pointerover', handleDotHover);
        dot.removeEventListener('pointerout', handleDotOut);
        dot.removeEventListener('click', handleSnapDotClick);
      }

      if (dotsGroup.parentNode) {
        dotsGroup.parentNode.removeChild(dotsGroup);
      }

      if (previewsGroup.parentNode) {
        previewsGroup.parentNode.removeChild(previewsGroup);
      }
    },
  };

  return handle;
}
